import type { User } from '@/types';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';
import {
  hasActiveWorkspaceMembership,
  isValidWorkspaceIdFormat,
  type WorkspaceRecord,
} from '@/lib/auth/workspaceAuthority';

/**
 * Accessible-workspace resolution (client UX mirror).
 *
 * Enumerates the workspaces a user may enter, then VALIDATES each one against
 * workspace lifecycle + active membership. Authority is derived ONLY from:
 *   - canonical account_role (organization_* accounts + their home workspace)
 *   - active workspace_member scoped relationships
 *   - workspace lifecycle (active only)
 *
 * It NEVER consults workspace_role / role / platform_role / persona keys /
 * local-storage values, and it NEVER auto-selects the first accessible
 * workspace as a default.
 *
 * Server RLS/RPC remains authoritative; see workspaceServerContract.ts.
 */

export interface AccessibleWorkspace {
  id: string;
  organizationId: string;
  status: 'active';
}

/** Landing decision derived from the validated accessible-workspace set. */
export type WorkspaceLanding =
  | { readonly kind: 'none' }
  | { readonly kind: 'one'; readonly workspace: AccessibleWorkspace }
  | { readonly kind: 'many'; readonly workspaces: readonly AccessibleWorkspace[] };

const ORGANIZATION_ACCOUNT_ROLES = new Set([
  'organization_owner',
  'organization_admin',
  'organization_member',
]);

/**
 * Collect the candidate workspace ids a user is potentially connected to,
 * BEFORE lifecycle/membership validation:
 *   - every active workspace_member relationship's resourceId
 *   - the home workspace_id of an organization account
 */
function candidateWorkspaceIds(user: User | null | undefined): string[] {
  if (!user) return [];
  const ids = new Set<string>();
  for (const rel of getActiveScopedRelationships(user, 'workspace')) {
    ids.add(rel.resourceId);
  }
  if (user.workspace_id && ORGANIZATION_ACCOUNT_ROLES.has(user.account_role ?? '')) {
    ids.add(user.workspace_id);
  }
  return [...ids];
}

/**
 * Return the VALIDATED accessible workspaces for a user. Each candidate is
 * checked for a valid id format, an existing + active workspace record, and
 * active membership. Suspended users and suspended/archived workspaces are
 * excluded. Order is deterministic (sorted by id) — callers must NOT treat
 * index 0 as a default selection.
 */
export function listAccessibleWorkspaces(
  user: User | null | undefined,
  lookupWorkspace: (workspaceId: string) => WorkspaceRecord | null,
): AccessibleWorkspace[] {
  if (!user || user.status === 'suspended') return [];
  const out: AccessibleWorkspace[] = [];
  for (const id of candidateWorkspaceIds(user)) {
    if (!isValidWorkspaceIdFormat(id)) continue;
    const record = lookupWorkspace(id);
    if (!record || record.status !== 'active') continue;
    if (!hasActiveWorkspaceMembership(user, record.id)) continue;
    out.push({ id: record.id, organizationId: record.organizationId, status: 'active' });
  }
  out.sort((a, b) => a.id.localeCompare(b.id));
  return out;
}

/**
 * Derive the deterministic landing decision from a validated set. Never
 * auto-selects the first of many — 2+ requires explicit selection.
 */
export function resolveWorkspaceLanding(
  workspaces: readonly AccessibleWorkspace[],
): WorkspaceLanding {
  if (workspaces.length === 0) return { kind: 'none' };
  if (workspaces.length === 1) return { kind: 'one', workspace: workspaces[0] };
  return { kind: 'many', workspaces };
}
